/**
 * Cart Manager - Handles quantity updates for cart dropdown and main cart
 * Instant updates, per-line-item updates, luxury animations
 */

class CartManager {
  constructor() {
    this.cartData = null;
    this.isUpdating = false;
    this.init();
  }

  init() {
    this.bindDropdownEvents();
    this.bindMainCartEvents();
    this.bindAddToCartForms();
    this.listenForUpdates();
    this.updateCartData();
  }

  bindAddToCartForms() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      // Don't AJAX forms inside the cart itself
      if (form.closest('.cart-dropdown') || form.closest('.cart-section')) return;

      e.preventDefault();

      const submitBtn = form.querySelector('button[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Wird hinzugefügt...';
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Add to cart failed');
        }

        const addedItem = await response.json();
        window.dispatchEvent(new CustomEvent('cart:item-added', { detail: addedItem }));

        const cart = await this.updateCartData();
        this.renderCartDropdown(cart);
        this.openCartDropdown();
        this.updateCartCount();
        this.updateCartTotal();

      } catch (error) {
        console.error('Add to cart error:', error);
        alert(error.message || 'Das Produkt konnte nicht zum Warenkorb hinzugefügt werden.');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    });
  }

  openCartDropdown() {
    const cartDropdown = document.getElementById('cart-dropdown');
    if (cartDropdown) {
      cartDropdown.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }

  renderCartDropdown(cart) {
    const content = document.querySelector('[data-cart-items]');
    const footer = document.querySelector('[data-cart-footer]');
    if (!content) return;

    if (!cart.items || cart.items.length === 0) {
      content.innerHTML = `
        <div class="cart-dropdown__empty">
          <div class="cart-dropdown__empty-icon">
            <i class="fa-solid fa-cart-shopping"></i>
          </div>
          <p class="cart-dropdown__empty-text">Ihr Warenkorb ist leer</p>
          <a href="/collections/all" class="cart-dropdown__btn cart-dropdown__btn--shop">
            <i class="fa-solid fa-store"></i> Jetzt shoppen
          </a>
        </div>
      `;
      if (footer) footer.style.display = 'none';
      return;
    }

    const itemsHtml = cart.items.map((item, index) => {
      const image = item.image ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" class="cart-dropdown__item-image" loading="lazy">` : '';
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<span class="cart-dropdown__item-variant">${this.escapeHtml(item.variant_title)}</span>`
        : '';

      return `
        <div class="cart-dropdown__item" data-line="${index + 1}" data-variant-id="${item.variant_id}">
          <div class="cart-dropdown__item-image">
            ${image}
          </div>
          <div class="cart-dropdown__item-info">
            <a href="${item.url}" class="cart-dropdown__item-title">${this.escapeHtml(item.product_title)}</a>
            ${variantTitle}
            <div class="cart-dropdown__item-controls">
              <button type="button" class="cart-qty-btn" data-action="decrease" aria-label="Menge verringern">
                <i class="fa-solid fa-minus"></i>
              </button>
              <span class="cart-qty-display" data-quantity>${item.quantity}</span>
              <button type="button" class="cart-qty-btn" data-action="increase" aria-label="Menge erhöhen">
                <i class="fa-solid fa-plus"></i>
              </button>
              <button type="button" class="cart-remove-btn" data-action="remove" aria-label="Artikel entfernen">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
            <div class="cart-dropdown__item-line-price" data-line-price>${this.formatMoney(item.final_line_price)}</div>
          </div>
        </div>
      `;
    }).join('');

    content.innerHTML = itemsHtml;

    if (footer) {
      footer.style.display = 'flex';
      footer.style.flexDirection = 'column';
      footer.style.opacity = '1';
      footer.style.transform = 'translateY(0)';
      footer.innerHTML = `
        <div class="cart-dropdown__total">
          <span>Gesamtsumme:</span>
          <span>${this.formatMoney(cart.total_price)}</span>
        </div>
        <div class="cart-dropdown__buttons">
          <a href="/cart" class="cart-dropdown__btn cart-dropdown__btn--secondary">
            <i class="fa-solid fa-cart-shopping"></i> Zum Warenkorb
          </a>
          <a href="/checkout" class="cart-dropdown__btn cart-dropdown__btn--primary">
            Zur Kasse <i class="fa-solid fa-arrow-right"></i>
          </a>
        </div>
      `;
    }
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async updateCartData() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.cartData = cart;
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      return this.cartData;
    }
  }

  // Handle dropdown quantity buttons
  bindDropdownEvents() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.cart-qty-btn, .cart-remove-btn');
      if (!btn) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const item = btn.closest('[data-line]');
      if (!item) return;
      
      const line = item.dataset.line;
      const variantId = item.dataset.variantId;
      const action = btn.dataset.action;
      
      await this.handleDropdownAction(line, variantId, action, item);
    });
  }

  async handleDropdownAction(line, variantId, action, itemElement) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    const isDropdown = itemElement.closest('.cart-dropdown') !== null;
    const isMainCart = itemElement.closest('.cart-section') !== null;

    const qtyDisplay = itemElement.querySelector('[data-quantity]');
    const qtyInput = itemElement.querySelector('input[type="number"]');
    const currentQty = qtyDisplay
      ? parseInt(qtyDisplay.textContent) || 0
      : (qtyInput ? parseInt(qtyInput.value) || 0 : 0);

    let newQty = currentQty;

    switch(action) {
      case 'decrease':
        newQty = Math.max(0, currentQty - 1);
        break;
      case 'increase':
        newQty = currentQty + 1;
        break;
      case 'remove':
        newQty = 0;
        break;
    }

    if (newQty === 0) {
      // Animate removal
      itemElement.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      itemElement.style.opacity = '0';
      itemElement.style.transform = isDropdown ? 'translateX(20px)' : 'translateX(-20px)';

      await this.updateCart(variantId, 0);

      setTimeout(() => {
        itemElement.remove();
        this.updateCartTotal();
        if (isDropdown) this.checkEmptyCart();
        if (isMainCart) this.checkEmptyMainCart();
      }, 300);
    } else {
      // Update UI quantity optimistically
      if (qtyDisplay) qtyDisplay.textContent = newQty;
      if (qtyInput) qtyInput.value = newQty;

      // Add visual feedback
      if (qtyDisplay) {
        qtyDisplay.style.transform = 'scale(1.2)';
        qtyDisplay.style.color = '#c9a227';
        setTimeout(() => {
          qtyDisplay.style.transform = 'scale(1)';
          qtyDisplay.style.color = '#162814';
        }, 200);
      }

      // Update cart in background first, then refresh prices from real data
      await this.updateCart(variantId, newQty);
      this.updateLinePrice(itemElement, newQty);
    }

    // Update cart count bubble
    this.updateCartCount();

    this.isUpdating = false;
  }

  async updateCart(variantId, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      const cart = await response.json();
      this.cartData = cart;
      
      // Dispatch update event for other components
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      
      return cart;
      
    } catch (error) {
      console.error('Cart update error:', error);
      // Could show error toast here
    }
  }

  updateLinePrice(itemElement, quantity) {
    // Use fresh cart data to show accurate line total
    const variantId = itemElement.dataset.variantId;
    const item = this.cartData?.items?.find(i => i.variant_id == variantId);
    
    if (item) {
      const totalEl = itemElement.querySelector('[data-line-price]');
      if (totalEl) {
        totalEl.textContent = this.formatMoney(item.final_line_price);
        
        // Flash animation
        totalEl.style.transition = 'all 0.2s ease';
        totalEl.style.color = '#c9a227';
        totalEl.style.transform = 'scale(1.05)';
        
        setTimeout(() => {
          totalEl.style.color = '#4a5d23';
          totalEl.style.transform = 'scale(1)';
        }, 200);
      }
    }
    
    // Update cart total
    this.updateCartTotal();
  }

  updateCartTotal() {
    if (!this.cartData) return;

    // Dropdown total
    const footerTotal = document.querySelector('.cart-dropdown__total span:last-child');
    if (footerTotal) {
      footerTotal.textContent = this.formatMoney(this.cartData.total_price);

      // Flash animation
      footerTotal.style.transition = 'all 0.2s ease';
      footerTotal.style.color = '#c9a227';
      footerTotal.style.transform = 'scale(1.05)';

      setTimeout(() => {
        footerTotal.style.color = '#4a5d23';
        footerTotal.style.transform = 'scale(1)';
      }, 200);
    }

    // Main cart total and subtotal
    const mainTotal = document.querySelector('[data-cart-total]');
    if (mainTotal) {
      mainTotal.textContent = this.formatMoney(this.cartData.total_price);
    }

    const mainSubtotal = document.querySelector('[data-cart-subtotal]');
    if (mainSubtotal) {
      mainSubtotal.textContent = this.formatMoney(this.cartData.items_subtotal_price);
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }
    if (isNaN(cents) || cents === null) {
      return '0,00 €';
    }

    const moneyFormat = window.theme && window.theme.money_format ? window.theme.money_format : '€{{ amount }}';
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return moneyFormat.replace(/\{\{\s*amount\w*\s*\}\}/,amount);
  }

  updateCartCount() {
    if (!this.cartData) return;
    
    const bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
    bubbles.forEach(bubble => {
      bubble.textContent = this.cartData.item_count;
    });
    
    // Animate bubble
    const bubbleContainers = document.querySelectorAll('.cart-count-bubble');
    bubbleContainers.forEach(bubble => {
      bubble.style.transition = 'transform 0.2s ease';
      bubble.style.transform = 'scale(1.3)';
      setTimeout(() => {
        bubble.style.transform = 'scale(1)';
      }, 200);
    });
  }

  checkEmptyCart() {
    const items = document.querySelectorAll('.cart-dropdown__item');
    if (items.length === 0) {
      const content = document.querySelector('[data-cart-items]');
      const footer = document.querySelector('[data-cart-footer]');
      
      if (content) {
        content.innerHTML = `
          <div class="cart-dropdown__empty">
            <div class="cart-dropdown__empty-icon">
              <i class="fa-solid fa-cart-shopping"></i>
            </div>
            <p class="cart-dropdown__empty-text">Ihr Warenkorb ist leer</p>
            <a href="/collections/all" class="cart-dropdown__btn cart-dropdown__btn--shop">
              <i class="fa-solid fa-store"></i> Jetzt shoppen
            </a>
          </div>
        `;
      }
      
      if (footer) {
        footer.style.opacity = '0';
        footer.style.transform = 'translateY(20px)';
        setTimeout(() => {
          footer.style.display = 'none';
        }, 300);
      }
    }
  }

  // Main cart page handlers
  bindMainCartEvents() {
    // Wait for DOM to be ready
    setTimeout(() => {
      // Prevent cart form reload on "Aktualisieren", allow checkout
      const cartForm = document.getElementById('cart');
      if (cartForm) {
        cartForm.addEventListener('submit', (e) => {
          const submitter = e.submitter;
          if (!submitter || submitter.name !== 'checkout') {
            e.preventDefault();
          }
        });
      }

      document.querySelectorAll('quantity-input, .quantity').forEach(input => {
        const minusBtn = input.querySelector('[name="minus"], .quantity__button[name="minus"]');
        const plusBtn = input.querySelector('[name="plus"], .quantity__button[name="plus"]');
        const qtyInput = input.querySelector('input[type="number"]');
        
        if (minusBtn) {
          minusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const current = parseInt(qtyInput.value) || 0;
            if (current > 0) {
              qtyInput.value = current - 1;
              this.handleMainCartChange(qtyInput);
            }
          });
        }
        
        if (plusBtn) {
          plusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const current = parseInt(qtyInput.value) || 0;
            qtyInput.value = current + 1;
            this.handleMainCartChange(qtyInput);
          });
        }
        
        // Direct input change (with debounce)
        let timeout;
        qtyInput?.addEventListener('change', () => {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            this.handleMainCartChange(qtyInput);
          }, 500);
        });
      });
    }, 500);
  }

  async handleMainCartChange(input) {
    const variantId = input.dataset.variantId || input.id?.replace('Quantity-', '');
    const quantity = parseInt(input.value) || 0;
    const row = input.closest('.cart-item');

    if (!variantId) return;

    await this.updateCart(variantId, quantity);

    // Update cart count
    this.updateCartCount();

    // Update line price and cart total from fresh data
    if (row && quantity > 0) {
      this.updateLinePrice(row, quantity);
    }
    this.updateCartTotal();

    // If quantity is 0, remove the row
    if (quantity === 0 && row) {
      row.style.transition = 'all 0.3s ease';
      row.style.opacity = '0';
      row.style.transform = 'translateX(-20px)';
      setTimeout(() => {
        row.remove();
        this.checkEmptyMainCart();
      }, 300);
    }
  }

  checkEmptyMainCart() {
    const items = document.querySelectorAll('.cart-item');
    if (items.length === 0) {
      const cartForm = document.getElementById('cart');
      const cartLayout = document.querySelector('.cart-layout');
      const cartEmpty = document.querySelector('.cart-empty');

      if (cartForm) cartForm.style.display = 'none';
      if (cartLayout) cartLayout.style.display = 'none';

      if (cartEmpty) {
        cartEmpty.style.display = 'block';
      } else if (cartLayout && cartLayout.parentElement) {
        cartLayout.parentElement.insertAdjacentHTML('beforeend', `
          <div class="cart-empty" style="text-align: center; padding: 80px 20px;">
            <div class="cart-empty__icon" style="font-size: 48px; color: #ccc; margin-bottom: 20px;">
              <i class="fa-solid fa-cart-shopping"></i>
            </div>
            <h2 style="margin-bottom: 15px;">Ihr Warenkorb ist leer</h2>
            <p style="margin-bottom: 30px;">Entdecken Sie unsere köstlichen Olivenöle.</p>
            <a href="/collections/all" class="btn btn-primary btn-lg">Jetzt shoppen</a>
          </div>
        `);
      }
    }
  }

  listenForUpdates() {
    window.addEventListener('cart:updated', (e) => {
      this.cartData = e.detail;
      this.updateCartCount();
      this.updateCartTotal();
    });
    
    // Also listen for add to cart events
    window.addEventListener('cart:item-added', () => {
      this.updateCartData().then(() => {
        this.updateCartCount();
        this.updateCartTotal();
      });
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartManager = new CartManager();
});

// Re-initialize if cart is dynamically updated
window.addEventListener('cart:reloaded', () => {
  if (window.cartManager) {
    window.cartManager.init();
  }
});
